import { Component, Prop, h, Watch } from '@stencil/core';
import { Steps, Step, LabelDisplayMode, LabelPosition } from './types';

@Component({
  tag: 'udp-stepper',
  styleUrl: 'udp-stepper.css',
  shadow: true,
})
export class UdpStepper {
  @Prop() steps: Steps;
  @Prop() activeStep: number = 0;
  @Prop() orientation: 'horizontal' | 'vertical' = 'horizontal';
  @Prop() alternativeLabel: boolean = false;
  @Prop() nonLinear: boolean = false;
  @Prop() completed: { [key: number]: boolean } = {};
  @Prop() disableStrictStepCompletion: boolean = false;
  @Prop() labelDisplay: LabelDisplayMode = 'all';
  @Prop() labelPosition: LabelPosition = 'default';
  @Prop() stepChange: (index: number) => void;

  @Watch('activeStep')
  validateActiveStep(newValue: number) {
    if (newValue < 0 || newValue >= this.steps.length) {
      console.warn('Invalid active step value');
      return;
    }
  }

  private handleStepClick(index: number) {
    if (this.canNavigateToStep(index) && this.stepChange) {
      this.stepChange(index);
    }
  }

  private canNavigateToStep(stepIndex: number): boolean {
    if (this.disableStrictStepCompletion || this.nonLinear) {
      return true;
    }

    for (let i = 0; i < stepIndex; i++) {
      if (!this.completed[i]) {
        return false;
      }
    }
    return true;
  }

  // ? This is the original renderIcon method - Unused at the moment
  // private renderIcon(step: Step, index: number) {
  //   const isCompleted = this.completed[index];
  //   const isActive = this.activeStep === index;
  //   const icon = isCompleted ? step.stepDisplay.completeIcon : step.stepDisplay.inProcessIcon;

  //   return (
  //     <div
  //       class={{
  //         'step-icon': true,
  //         'completed': isCompleted,
  //         'active': isActive,
  //       }}
  //     >
  //       {icon}
  //     </div>
  //   );
  // }

  private renderStepHeader(step: Step, index: number) {
    const isCompleted = this.completed[index];
    const isActive = this.activeStep === index;
    const isClickable = this.canNavigateToStep(index);

    return (
      <div
        class={{
          'step-header': true,
          'icons-only': this.labelDisplay === 'none',
          'active': isActive && this.labelDisplay === 'active-only',
          'completed': isCompleted,
          'active-only': this.labelDisplay === 'active-only' && isActive,
          'disabled': !isClickable,
          'label-bottom': this.labelPosition === 'bottom',
          'label-right': this.labelPosition === 'right',
        }}
        onClick={() => {
          if (isClickable) {
            this.handleStepClick(index);
          }
        }}
      >
        <div
          class={{
            'step-icon': true,
            'active': isActive,
            'completed': isCompleted,
          }}
        >
          <unity-typography variant="body1" color={isCompleted ? 'inherit' : isActive ? 'primary' : 'textSecondary'}>
            {isCompleted ? step.stepDisplay.completeIcon : step.stepDisplay.inProcessIcon}
          </unity-typography>
        </div>
        {this.labelDisplay === 'all' && (
          <div class="step-labels">
            <unity-typography variant="body1" bold>
              {step.stepDisplay.title}
            </unity-typography>
            {step.stepDisplay.subTitle && (
              <unity-typography variant="caption-text" color="textSecondary">
                {step.stepDisplay.subTitle}
              </unity-typography>
            )}
          </div>
        )}
        {this.labelDisplay === 'active-only' && isActive && (
          <div class="step-labels">
            <unity-typography variant="body1" bold>
              {step.stepDisplay.title}
            </unity-typography>
            {step.stepDisplay.subTitle && (
              <unity-typography variant="caption-text" color="textSecondary">
                {step.stepDisplay.subTitle}
              </unity-typography>
            )}
          </div>
        )}
      </div>
    );
  }

  private renderStepContent(step: Step, index: number) {
    const isActive = this.activeStep === index;
    if (!isActive) {
      return <div style={{ display: 'none' }} class="step-content-wrapper"></div>;
    }

    return (
      <div class="step-content-wrapper">
        <div class="step-content-display">{step?.content ? step?.content : <slot />}</div>
        {step.buttons && (
          <div class="step-buttons">
            {step.buttons.map(button => (
              <custom-button
                variant={button.variant || 'default'}
                disabled={button.disabled}
                onClick={e => {
                  e.stopPropagation();
                  if (button.customAction) {
                    button.customAction();
                  } else {
                    this.handleStepClick(button.action - 1);
                  }
                }}
              >
                {button.title}
              </custom-button>
            ))}
          </div>
        )}
      </div>
    );
  }

  private renderHorizontalStepper() {
    return (
      <div class="stepper-container horizontal">
        <div class="step-headers">{this.steps.map((step, index) => this.renderStepHeader(step, index))}</div>
        <div class="step-content-container">{this.steps.map((step, index) => this.renderStepContent(step, index))}</div>
      </div>
    );
  }

  private renderVerticalStepper() {
    return (
      <div class="stepper-container vertical">
        {this.steps.map((step, index) => (
          <div class="step vertical">
            <div class="step-header-container">{this.renderStepHeader(step, index)}</div>
            <div class="step-content-vertical">{this.activeStep === index && this.renderStepContent(step, index)}</div>
          </div>
        ))}
      </div>
    );
  }

  render() {
    return this.orientation === 'horizontal' ? this.renderHorizontalStepper() : this.renderVerticalStepper();
  }
}
